// Lightweight UI translation. Dictionaries are keyed by the English string, so
// a missing entry simply renders English: untranslated screens degrade, they
// never break. Only the app shell (navigation, header, command palette) and the
// Localisation settings are translated so far; extend a dictionary to cover
// more, no component API changes needed.
import type { LanguageCode } from '@/lib/locale';
import { arApp } from './i18n-ar-app.ts';

type Dictionary = Record<string, string>;

const ar: Dictionary = {
  // sections
  'Today': 'اليوم', 'Communicate': 'التواصل', 'Deliver': 'التنفيذ', 'Decide': 'القرار', 'Govern & Trace': 'الحوكمة والتتبّع', 'Admin': 'الإدارة',
  'What needs my attention today?': 'ما الذي يحتاج إلى اهتمامي اليوم؟',
  "What's being said, and by whom?": 'ما الذي يُقال، ومن يقوله؟',
  'Are we on track, and what could go wrong?': 'هل نسير في المسار الصحيح، وما الذي قد يسوء؟',
  'What needs a decision, and why?': 'ما الذي يحتاج إلى قرار، ولماذا؟',
  'Who owns it, and what is the evidence?': 'من المسؤول، وما الدليل؟',
  'How is this workspace configured?': 'كيف تم إعداد مساحة العمل هذه؟',
  // views
  'Portfolio Overview': 'نظرة عامة على المحفظة', 'My Work': 'أعمالي', 'Weekly Digest': 'الملخص الأسبوعي', 'Reports': 'التقارير', 'Calendar': 'التقويم',
  'Overview': 'نظرة عامة', 'Inbox': 'البريد الوارد', 'Meetings': 'الاجتماعات', 'Stakeholders': 'أصحاب المصلحة', 'Team': 'الفريق',
  'Projects': 'المشاريع', 'Portfolio': 'المحفظة', 'Risks': 'المخاطر', 'Signals': 'الإشارات', 'Dependencies': 'الاعتماديات', 'Strategy': 'الاستراتيجية',
  'Decision Board': 'لوحة القرارات', 'Traceability': 'إمكانية التتبّع', 'Decisions': 'القرارات', 'Actions & Issues': 'الإجراءات والمشكلات', 'Approvals': 'الموافقات',
  'Document Intelligence': 'ذكاء المستندات', 'Audit Trail': 'سجل التدقيق', 'Compliance': 'الامتثال', 'Activity': 'النشاط', 'Documents': 'المستندات',
  'Knowledge Base': 'قاعدة المعرفة', 'Analytics': 'التحليلات', 'AI Controls': 'ضوابط الذكاء الاصطناعي', 'Branding': 'الهوية البصرية', 'Organisation': 'المؤسسة',
  // shell
  'Integrations': 'التكاملات', 'Go to a page…': 'انتقل إلى صفحة…', 'Connect sources': 'اربط المصادر', 'Connected': 'متصل', 'Needs attention': 'يحتاج إلى اهتمام', 'Paused': 'متوقف مؤقتًا',
  'Open navigation menu': 'فتح قائمة التنقل', 'Switch to light theme': 'التبديل إلى المظهر الفاتح', 'Switch to dark theme': 'التبديل إلى المظهر الداكن', 'User': 'مستخدم',
  'Primary': 'الرئيسية',
  // settings
  'Console': 'لوحة التحكم', 'Connected sources': 'المصادر المتصلة', 'Checking connections…': 'جارٍ فحص الاتصالات…', 'Signed in as': 'تم تسجيل الدخول بصفة', 'No matching page.': 'لا توجد صفحة مطابقة.', 'Workspace': 'مساحة العمل', 'Search pages (Cmd or Ctrl + K)': 'بحث في الصفحات (Cmd أو Ctrl + K)',
  'Total Budget': 'إجمالي الميزانية',
  "Hi! I'm Masira AI. How can I help you with your projects today?": "هلا! أنا Masira AI. وش أقدر أساعدك فيه في مشاريعك اليوم؟",
  "Answers use only your connected workspace data.": "الإجابات تعتمد بس على بيانات مساحة عملك المرتبطة.",
  "Ask about your projects…": "اسأل عن مشاريعك…",
  "Type a message...": "اكتب رسالتك...",
  "private workspace assistant": "مساعد مساحة العمل الخاصة",
  "Your project management assistant": "مساعدك لإدارة المشاريع",
  "Collapse Ask Masira panel": "طي لوحة اسأل Masira",
  "Ask Masira": "اسأل Masira",
  "Masira AI": "Masira AI",
  "Create a report": "إنشاء تقرير",
  "Do something else": "شي ثاني",
  "Tell me more": "زدني تفاصيل",
  "Open {view}": "افتح {view}",
  "Tell me more about that, with the key details.": "زدني تفاصيل أكثر عن هالموضوع، مع أهم النقاط.",
  "What could go wrong?": "وش اللي ممكن يصير غلط؟",
  "Which risks are new this week?": "وش المخاطر الجديدة هالأسبوع؟",
  "What mitigations are overdue?": "وش إجراءات المعالجة المتأخرة؟",
  "Which projects are off track?": "أي المشاريع خارج المسار؟",
  "Which projects are over budget?": "أي المشاريع تجاوزت الميزانية؟",
  "What is blocked right now?": "وش المتعطل الحين؟",
  "Compare project health": "قارن صحة المشاريع",
  "Where is budget at risk?": "وين الميزانية معرّضة للخطر؟",
  "Which signals need action?": "أي الإشارات تحتاج إجراء؟",
  "Why was the latest signal raised?": "ليش انرفعت آخر إشارة؟",
  "What needs a reply today?": "وش اللي يحتاج رد اليوم؟",
  "Summarise unread emails": "لخّص الإيميلات غير المقروءة",
  "What actions came out of recent meetings?": "وش الإجراءات اللي طلعت من الاجتماعات الأخيرة؟",
  "Prepare me for my next meeting": "جهّزني لاجتماعي الجاي",
  "Who needs an update?": "مين يحتاج تحديث؟",
  "Which stakeholders are at risk of disengaging?": "أي أصحاب المصلحة ممكن يبتعدون؟",
  "Which decisions are overdue?": "أي القرارات متأخرة؟",
  "Summarise recent decisions": "لخّص القرارات الأخيرة",
  "What is waiting for my approval?": "وش ينتظر موافقتي؟",
  "Which approvals are oldest?": "أي الموافقات الأقدم؟",
  "Which report suits my audience?": "أي تقرير يناسب الجمهور عندي؟",
  "What changed in the last 24 hours?": "وش تغيّر في آخر 24 ساعة؟",
  "Who approved the latest AI output?": "مين وافق على آخر مخرجات الذكاء الاصطناعي؟",
  "What is due this week?": "وش المستحق هالأسبوع؟",
  "Summarise recent meetings": "لخّص الاجتماعات الأخيرة",
  "Are we on track?": "هل احنا ماشين صح؟",
  "Draft a status update": "اكتب مسودة تحديث للحالة",
  "What decisions are pending?": "وش القرارات المعلّقة؟",
  "What changed recently?": "وش اللي تغيّر مؤخرًا؟",
  "Which items lack evidence?": "أي البنود ينقصها دليل؟",
  "Show compliance gaps": "وريني فجوات الامتثال",
  "Which integrations need attention?": "أي التكاملات تحتاج اهتمام؟",
  "Who has access to what?": "مين عنده صلاحية على إيش؟",
  "Summarise the biggest project risks": "لخّص أكبر مخاطر المشاريع",
  "Which risks should I look at?": "أي المخاطر أطالع عليها؟",
  "What would you like to do?": "وش تبغى تسوي؟",
  "Top 3 risks": "أهم 3 مخاطر",
  "Overdue mitigations": "إجراءات معالجة متأخرة",
  "Risks by project": "المخاطر حسب المشروع",
  "List the top 3 open risks across my projects, one line each with severity.": "اذكر لي أهم 3 مخاطر مفتوحة في مشاريعي، سطر لكل خطر مع مستوى الخطورة.",
  "Which risk mitigations are overdue? One line each.": "أي إجراءات معالجة المخاطر متأخرة؟ سطر لكل إجراء.",
  "Group the open risks by project, one line per project.": "جمّع المخاطر المفتوحة حسب المشروع، سطر لكل مشروع.",
  "Your briefing for {date}": "ملخصك ليوم {date}",
  "from your connected data": "من بياناتك المرتبطة",
  "Nothing to brief yet. Connect a source (Jira, Microsoft 365, Azure DevOps) and Masira will summarise communication, risks and decisions here every day.": "ما فيه شي نلخصه للحين. اربط مصدر (Jira أو Microsoft 365 أو Azure DevOps) وMasira بيلخّص لك التواصل والمخاطر والقرارات هنا كل يوم.",
  "Some sections could not be loaded; the numbers above may be incomplete.": "بعض الأقسام ما انحمّلت؛ ممكن الأرقام أعلاه تكون ناقصة.",
  "{n} project: {onTrack} on track, {atRisk} at risk, {delayed} delayed.": "{n} مشروع: {onTrack} على المسار، {atRisk} معرّض للخطر، {delayed} متأخر.",
  "{n} projects: {onTrack} on track, {atRisk} at risk, {delayed} delayed.": "{n} مشاريع: {onTrack} على المسار، {atRisk} معرّضة للخطر، {delayed} متأخرة.",
  "{n} open risk ({high} high or critical)": "{n} خطر مفتوح ({high} عالي أو حرج)",
  "{n} open risks ({high} high or critical)": "{n} مخاطر مفتوحة ({high} عالية أو حرجة)",
  ", {new} new in the last 24 hours": "، {new} جديدة في آخر 24 ساعة",
  "{n} AI output awaiting review": "{n} مخرجات ذكاء اصطناعي بانتظار المراجعة",
  "{n} AI outputs awaiting review": "{n} مخرجات ذكاء اصطناعي بانتظار المراجعة",
  "{n} decision pending": "{n} قرار معلّق",
  "{n} decisions pending": "{n} قرارات معلّقة",
  "{n} blocked task": "{n} مهمة متعطلة",
  "{n} blocked tasks": "{n} مهام متعطلة",
  "Waiting on you or your team: {items}.": "بانتظارك أو بانتظار فريقك: {items}.",
  "{n} unread message.": "{n} رسالة غير مقروءة.",
  "{n} unread messages.": "{n} رسائل غير مقروءة.",
  "Communication": "التواصل",
  "Open inbox": "افتح البريد",
  "No unread messages or recent meetings.": "ما فيه رسائل غير مقروءة أو اجتماعات حديثة.",
  "Recent meetings": "الاجتماعات الأخيرة",
  "{n} action item": "{n} بند إجراء",
  "{n} action items": "{n} بنود إجراء",
  "(no subject)": "(بدون عنوان)",
  "Escalated": "مُصعَّد",
  "All risks": "كل المخاطر",
  "No open risks. Risk signals appear here when a rule detects a problem.": "ما فيه مخاطر مفتوحة. تظهر إشارات المخاطر هنا لما تكتشف قاعدة مشكلة.",
  "detected {ago}": "اكتُشف {ago}",
  "Needs your attention": "يحتاج اهتمامك",
  "Nothing is waiting on you.": "ما فيه شي ينتظرك.",
  "AI output awaiting review": "مخرجات ذكاء اصطناعي بانتظار المراجعة",
  "Decision pending": "قرار معلّق",
  "Blocked": "متعطلة",
  "Overdue since {date}": "متأخرة منذ {date}",
  "Portfolio overview": "نظرة عامة على المحفظة",
  "No projects yet.": "ما فيه مشاريع للحين.",
  "{name} progress": "تقدم {name}",
  "Loading today's summary": "جارٍ تحميل ملخص اليوم",
  "critical": "حرج",
  "high": "عالي",
  "medium": "متوسط",
  "low": "منخفض",
  "info": "معلومة",
  "on-track": "على المسار",
  "at-risk": "معرّض للخطر",
  "delayed": "متأخر",
  "n/a": "—",
  'Localisation': 'التوطين', 'Language': 'اللغة', 'Text direction': 'اتجاه النص', 'Currency': 'العملة', 'Preview': 'معاينة', 'Save changes': 'حفظ التغييرات',
  'Automatic (follows language)': 'تلقائي (حسب اللغة)', 'Left to right': 'من اليسار إلى اليمين', 'Right to left': 'من اليمين إلى اليسار',
};

const fr: Dictionary = {
  'Today': "Aujourd'hui", 'Communicate': 'Communiquer', 'Deliver': 'Livrer', 'Decide': 'Décider', 'Govern & Trace': 'Gouvernance et traçabilité', 'Admin': 'Administration',
  'What needs my attention today?': "Qu'est-ce qui demande mon attention aujourd'hui ?",
  "What's being said, and by whom?": 'Que dit-on, et qui le dit ?',
  'Are we on track, and what could go wrong?': 'Sommes-nous dans les temps, et que pourrait-il mal tourner ?',
  'What needs a decision, and why?': "Qu'est-ce qui exige une décision, et pourquoi ?",
  'Who owns it, and what is the evidence?': 'Qui en est responsable, et quelles sont les preuves ?',
  'How is this workspace configured?': 'Comment cet espace de travail est-il configuré ?',
  'Portfolio Overview': 'Vue du portefeuille', 'My Work': 'Mon travail', 'Weekly Digest': 'Synthèse hebdomadaire', 'Reports': 'Rapports', 'Calendar': 'Calendrier',
  'Overview': "Vue d'ensemble", 'Inbox': 'Boîte de réception', 'Meetings': 'Réunions', 'Stakeholders': 'Parties prenantes', 'Team': 'Équipe',
  'Projects': 'Projets', 'Portfolio': 'Portefeuille', 'Risks': 'Risques', 'Signals': 'Signaux', 'Dependencies': 'Dépendances', 'Strategy': 'Stratégie',
  'Decision Board': 'Tableau des décisions', 'Traceability': 'Traçabilité', 'Decisions': 'Décisions', 'Actions & Issues': 'Actions et problèmes', 'Approvals': 'Approbations',
  'Document Intelligence': 'Intelligence documentaire', 'Audit Trail': "Journal d'audit", 'Compliance': 'Conformité', 'Activity': 'Activité', 'Documents': 'Documents',
  'Knowledge Base': 'Base de connaissances', 'Analytics': 'Analyses', 'AI Controls': "Contrôles de l'IA", 'Branding': 'Image de marque', 'Organisation': 'Organisation',
  'Integrations': 'Intégrations', 'Go to a page…': 'Aller à une page…', 'Connect sources': 'Connecter des sources', 'Connected': 'Connecté', 'Needs attention': 'Nécessite une action', 'Paused': 'En pause',
  'Open navigation menu': 'Ouvrir le menu de navigation', 'Switch to light theme': 'Passer au thème clair', 'Switch to dark theme': 'Passer au thème sombre', 'User': 'Utilisateur',
  'Primary': 'Principale',
  'Console': 'Console', 'Connected sources': 'Sources connectées', 'Checking connections…': 'Vérification des connexions…', 'Signed in as': 'Connecté en tant que', 'No matching page.': 'Aucune page correspondante.', 'Workspace': 'Espace de travail', 'Search pages (Cmd or Ctrl + K)': 'Rechercher des pages (Cmd ou Ctrl + K)',
  'Total Budget': 'Budget total',
  "Hi! I'm Masira AI. How can I help you with your projects today?": "Bonjour ! Je suis Masira AI. Comment puis-je vous aider sur vos projets aujourd'hui ?",
  "Answers use only your connected workspace data.": "Les réponses reposent uniquement sur les données connectées de votre espace.",
  "Ask about your projects…": "Posez une question sur vos projets…",
  "Type a message...": "Écrivez un message...",
  "Ask Masira": "Demander à Masira",
  "Open {view}": "Ouvrir {view}",
  "Create a report": "Créer un rapport",
  "Do something else": "Autre chose",
  "Tell me more": "En savoir plus",
  "Your briefing for {date}": "Votre point du {date}",
  "from your connected data": "à partir de vos données connectées",
  "Communication": "Communication",
  "Open inbox": "Ouvrir la boîte de réception",
  "Recent meetings": "Réunions récentes",
  "All risks": "Tous les risques",
  "Needs your attention": "À traiter",
  "Portfolio overview": "Vue du portefeuille",
  "No projects yet.": "Aucun projet pour l'instant.",
  "detected {ago}": "détecté {ago}",
  "critical": "critique",
  "high": "élevé",
  "medium": "moyen",
  "low": "faible",
  "on-track": "dans les temps",
  "at-risk": "à risque",
  "delayed": "en retard",
  'Localisation': 'Localisation', 'Language': 'Langue', 'Text direction': 'Sens du texte', 'Currency': 'Devise', 'Preview': 'Aperçu', 'Save changes': 'Enregistrer',
  'Automatic (follows language)': 'Automatique (selon la langue)', 'Left to right': 'De gauche à droite', 'Right to left': 'De droite à gauche',
};

const es: Dictionary = {
  'Today': 'Hoy', 'Communicate': 'Comunicar', 'Deliver': 'Entregar', 'Decide': 'Decidir', 'Govern & Trace': 'Gobernanza y trazabilidad', 'Admin': 'Administración',
  'What needs my attention today?': '¿Qué necesita mi atención hoy?',
  "What's being said, and by whom?": '¿Qué se dice y quién lo dice?',
  'Are we on track, and what could go wrong?': '¿Vamos por buen camino y qué podría salir mal?',
  'What needs a decision, and why?': '¿Qué requiere una decisión y por qué?',
  'Who owns it, and what is the evidence?': '¿Quién es el responsable y cuál es la evidencia?',
  'How is this workspace configured?': '¿Cómo está configurado este espacio de trabajo?',
  'Portfolio Overview': 'Resumen del portafolio', 'My Work': 'Mi trabajo', 'Weekly Digest': 'Resumen semanal', 'Reports': 'Informes', 'Calendar': 'Calendario',
  'Overview': 'Resumen', 'Inbox': 'Bandeja de entrada', 'Meetings': 'Reuniones', 'Stakeholders': 'Partes interesadas', 'Team': 'Equipo',
  'Projects': 'Proyectos', 'Portfolio': 'Portafolio', 'Risks': 'Riesgos', 'Signals': 'Señales', 'Dependencies': 'Dependencias', 'Strategy': 'Estrategia',
  'Decision Board': 'Tablero de decisiones', 'Traceability': 'Trazabilidad', 'Decisions': 'Decisiones', 'Actions & Issues': 'Acciones y problemas', 'Approvals': 'Aprobaciones',
  'Document Intelligence': 'Inteligencia documental', 'Audit Trail': 'Registro de auditoría', 'Compliance': 'Cumplimiento', 'Activity': 'Actividad', 'Documents': 'Documentos',
  'Knowledge Base': 'Base de conocimiento', 'Analytics': 'Analítica', 'AI Controls': 'Controles de IA', 'Branding': 'Marca', 'Organisation': 'Organización',
  'Integrations': 'Integraciones', 'Go to a page…': 'Ir a una página…', 'Connect sources': 'Conectar fuentes', 'Connected': 'Conectado', 'Needs attention': 'Requiere atención', 'Paused': 'En pausa',
  'Open navigation menu': 'Abrir menú de navegación', 'Switch to light theme': 'Cambiar a tema claro', 'Switch to dark theme': 'Cambiar a tema oscuro', 'User': 'Usuario',
  'Primary': 'Principal',
  'Console': 'Consola', 'Connected sources': 'Fuentes conectadas', 'Checking connections…': 'Comprobando conexiones…', 'Signed in as': 'Sesión iniciada como', 'No matching page.': 'Ninguna página coincide.', 'Workspace': 'Espacio de trabajo', 'Search pages (Cmd or Ctrl + K)': 'Buscar páginas (Cmd o Ctrl + K)',
  'Total Budget': 'Presupuesto total',
  "Hi! I'm Masira AI. How can I help you with your projects today?": "¡Hola! Soy Masira AI. ¿Cómo puedo ayudarte con tus proyectos hoy?",
  "Answers use only your connected workspace data.": "Las respuestas usan solo los datos conectados de tu espacio de trabajo.",
  "Ask about your projects…": "Pregunta sobre tus proyectos…",
  "Type a message...": "Escribe un mensaje...",
  "Ask Masira": "Preguntar a Masira",
  "Open {view}": "Abrir {view}",
  "Create a report": "Crear un informe",
  "Do something else": "Otra cosa",
  "Tell me more": "Cuéntame más",
  "Your briefing for {date}": "Tu resumen del {date}",
  "from your connected data": "a partir de tus datos conectados",
  "Communication": "Comunicación",
  "Open inbox": "Abrir bandeja",
  "Recent meetings": "Reuniones recientes",
  "All risks": "Todos los riesgos",
  "Needs your attention": "Requiere tu atención",
  "Portfolio overview": "Resumen del portafolio",
  "No projects yet.": "Aún no hay proyectos.",
  "detected {ago}": "detectado {ago}",
  "critical": "crítico",
  "high": "alto",
  "medium": "medio",
  "low": "bajo",
  "on-track": "en camino",
  "at-risk": "en riesgo",
  "delayed": "retrasado",
  'Localisation': 'Localización', 'Language': 'Idioma', 'Text direction': 'Dirección del texto', 'Currency': 'Moneda', 'Preview': 'Vista previa', 'Save changes': 'Guardar cambios',
  'Automatic (follows language)': 'Automática (según el idioma)', 'Left to right': 'De izquierda a derecha', 'Right to left': 'De derecha a izquierda',
};

const de: Dictionary = {
  'Today': 'Heute', 'Communicate': 'Kommunikation', 'Deliver': 'Umsetzung', 'Decide': 'Entscheidung', 'Govern & Trace': 'Governance & Nachverfolgung', 'Admin': 'Verwaltung',
  'What needs my attention today?': 'Was braucht heute meine Aufmerksamkeit?',
  "What's being said, and by whom?": 'Was wird gesagt, und von wem?',
  'Are we on track, and what could go wrong?': 'Liegen wir im Plan, und was könnte schiefgehen?',
  'What needs a decision, and why?': 'Was erfordert eine Entscheidung, und warum?',
  'Who owns it, and what is the evidence?': 'Wer ist verantwortlich, und was ist der Nachweis?',
  'How is this workspace configured?': 'Wie ist dieser Arbeitsbereich konfiguriert?',
  'Portfolio Overview': 'Portfolioübersicht', 'My Work': 'Meine Aufgaben', 'Weekly Digest': 'Wochenüberblick', 'Reports': 'Berichte', 'Calendar': 'Kalender',
  'Overview': 'Übersicht', 'Inbox': 'Posteingang', 'Meetings': 'Besprechungen', 'Stakeholders': 'Stakeholder', 'Team': 'Team',
  'Projects': 'Projekte', 'Portfolio': 'Portfolio', 'Risks': 'Risiken', 'Signals': 'Signale', 'Dependencies': 'Abhängigkeiten', 'Strategy': 'Strategie',
  'Decision Board': 'Entscheidungsboard', 'Traceability': 'Nachverfolgbarkeit', 'Decisions': 'Entscheidungen', 'Actions & Issues': 'Maßnahmen & Probleme', 'Approvals': 'Freigaben',
  'Document Intelligence': 'Dokumentenintelligenz', 'Audit Trail': 'Prüfprotokoll', 'Compliance': 'Compliance', 'Activity': 'Aktivität', 'Documents': 'Dokumente',
  'Knowledge Base': 'Wissensdatenbank', 'Analytics': 'Analysen', 'AI Controls': 'KI-Steuerung', 'Branding': 'Branding', 'Organisation': 'Organisation',
  'Integrations': 'Integrationen', 'Go to a page…': 'Zu einer Seite wechseln…', 'Connect sources': 'Quellen verbinden', 'Connected': 'Verbunden', 'Needs attention': 'Erfordert Aufmerksamkeit', 'Paused': 'Pausiert',
  'Open navigation menu': 'Navigationsmenü öffnen', 'Switch to light theme': 'Zum hellen Design wechseln', 'Switch to dark theme': 'Zum dunklen Design wechseln', 'User': 'Benutzer',
  'Primary': 'Hauptnavigation',
  'Console': 'Konsole', 'Connected sources': 'Verbundene Quellen', 'Checking connections…': 'Verbindungen werden geprüft…', 'Signed in as': 'Angemeldet als', 'No matching page.': 'Keine passende Seite.', 'Workspace': 'Arbeitsbereich', 'Search pages (Cmd or Ctrl + K)': 'Seiten durchsuchen (Cmd oder Strg + K)',
  'Total Budget': 'Gesamtbudget',
  "Hi! I'm Masira AI. How can I help you with your projects today?": "Hallo! Ich bin Masira AI. Wie kann ich Ihnen heute bei Ihren Projekten helfen?",
  "Answers use only your connected workspace data.": "Antworten nutzen nur die verbundenen Daten Ihres Arbeitsbereichs.",
  "Ask about your projects…": "Fragen zu Ihren Projekten…",
  "Type a message...": "Nachricht schreiben...",
  "Ask Masira": "Masira fragen",
  "Open {view}": "{view} öffnen",
  "Create a report": "Bericht erstellen",
  "Do something else": "Etwas anderes",
  "Tell me more": "Mehr dazu",
  "Your briefing for {date}": "Ihr Überblick für {date}",
  "from your connected data": "aus Ihren verbundenen Daten",
  "Communication": "Kommunikation",
  "Open inbox": "Posteingang öffnen",
  "Recent meetings": "Letzte Besprechungen",
  "All risks": "Alle Risiken",
  "Needs your attention": "Braucht Ihre Aufmerksamkeit",
  "Portfolio overview": "Portfolioübersicht",
  "No projects yet.": "Noch keine Projekte.",
  "detected {ago}": "erkannt {ago}",
  "critical": "kritisch",
  "high": "hoch",
  "medium": "mittel",
  "low": "niedrig",
  "on-track": "im Plan",
  "at-risk": "gefährdet",
  "delayed": "verzögert",
  'Localisation': 'Lokalisierung', 'Language': 'Sprache', 'Text direction': 'Textrichtung', 'Currency': 'Währung', 'Preview': 'Vorschau', 'Save changes': 'Änderungen speichern',
  'Automatic (follows language)': 'Automatisch (nach Sprache)', 'Left to right': 'Von links nach rechts', 'Right to left': 'Von rechts nach links',
};

const DICTIONARIES: Partial<Record<LanguageCode, Dictionary>> = { ar: { ...arApp, ...ar }, fr, es, de };

const fill = (text: string, params?: Record<string, string | number>) =>
  params ? text.replace(/\{(\w+)\}/g, (m, k) => (k in params ? String(params[k]) : m)) : text;

/** Translate an English UI string; unknown strings and English return as-is. `{name}` tokens are filled from params. */
export function translate(language: LanguageCode, text: string, params?: Record<string, string | number>): string {
  const dict = DICTIONARIES[language];
  const hit = dict?.[text];
  if (hit !== undefined) return fill(hit, params);
  // "Open <view label>" pills are built from a view label, so translate the label and the verb separately.
  const open = text.match(/^Open (.+)$/);
  if (dict && open && dict['Open {view}']) return fill(dict['Open {view}'], { ...params, view: translate(language, open[1]) });
  return fill(text, params);
}

const SOURCE_WORD: Partial<Record<LanguageCode, string>> = { ar: 'المصدر', fr: 'Source', es: 'Fuente', de: 'Quelle' };

/**
 * The model cites documents as "(Source: <file name>)". In an Arabic answer that Latin-led parenthetical mixes
 * directions and renders with the brackets and full stop in the wrong places, so localise the label and isolate
 * the citation (RLI..PDI for Arabic) so it lays out as one right-to-left unit.
 */
export function localizeCitations(language: LanguageCode, text: string): string {
  const word = SOURCE_WORD[language];
  if (!word) return text;
  return text.replace(/\(Source:\s*([^)]*)\)/g, (_m, name: string) =>
    language === 'ar' ? `\u2067(${word}: ${name.trim()})\u2069` : `(${word}: ${name.trim()})`);
}
